import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import DefaultData from '../../CompanyData/DefaultData';

const initialHajjFieldValues = {
    packageEnquiryId: "00000000-0000-0000-0000-000000000000",
    packageId: null,
    companyId: null,
    name: '',
    email: '',
    phoneNumber: '',
    nationality: '',
    comments: '',
    profileFile: null,
    passportFrontFile: null,
    passportBackFile: null,
    aadharFile: null,
    vaccinationFile: null,
    type: 'Hajj' // Default type for Hajj enquiries
};


const CheckmarkIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#0c4b33" strokeWidth="2" />
        <path d="M7.5 12.5L10.5 15.5L16.5 9.5" stroke="#0c4b33" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const HajjEnquiryModal = ({ show, handleClose, packageDetails }) => {
    const [values, setValues] = useState(initialHajjFieldValues);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    
    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setValues({ ...values, [name]: files[0] || null });
    };

    const resetForm = () => {
        setValues(initialHajjFieldValues);
        setErrors({});
        setIsSubmitted(false);
    }

    useEffect(() => {
        if (show) {
            resetForm();
        }
    }, [show]);

    const validate = () => {
        let tempErrors = {};
        if (!values.name) tempErrors.name = "Name is required.";
        if (!values.phoneNumber) tempErrors.phoneNumber = "Phone number is required.";
        if (!values.email) {
            tempErrors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(values.email)) {
            tempErrors.email = "Email is not valid.";
        }
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        const packageId = packageDetails?.package?.packageId;
        const companyId = DefaultData.DEFAULTCOMPANYID;

        setIsSubmitting(true);

        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('email', values.email);
        formData.append('phoneNumber', values.phoneNumber);
        formData.append('packageId', packageId);
        formData.append('companyId', companyId);
        formData.append('type', values.type); // Ensure type is included
        
        if (values.nationality) formData.append('nationality', values.nationality);
        if (values.comments) formData.append('comments', values.comments);
        if (values.profileFile) formData.append('ProfileFile', values.profileFile);
        if (values.passportFrontFile) formData.append('PassportFrontFile', values.passportFrontFile);
        if (values.passportBackFile) formData.append('PassportBackFile', values.passportBackFile);
        if (values.aadharFile) formData.append('AadharFile', values.aadharFile);
        if (values.vaccinationFile) formData.append('VaccinationFile', values.vaccinationFile);

        try {
           
            const response = await apiService.post(config.CREATEPACKAGEENQUIRY, formData);
            if (response) {
                setIsSubmitted(true);
            }
        } catch (error) {
            console.error('Enquiry submission failed:', error);
            setErrors({ api: "Failed to submit enquiry. Please check your connection and try again." });
        } finally {
            setIsSubmitting(false);
        }
    };

    // The Thank You view can be reused or customized
    const ThankYouView = () => (
        <div className="text-center p-4">
            <h2 className="fw-bold mb-3">Thank You! Your Enquiry is Received.</h2>
            <p className="lead mb-4">
                Our Hajj expert will contact you shortly on <strong>{values.phoneNumber}</strong>.
            </p>
            <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', display: 'inline-block', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div className="d-flex align-items-center">
                    <div className="flex-grow-1 text-start">
                        <h6 className="fw-bold mb-0">Request for: {packageDetails?.package?.packageName}</h6>
                        <span className="text-muted">We will get back to you with the details.</span>
                    </div>
                    <div className="ms-4"><CheckmarkIcon /></div>
                </div>
            </div>
        </div>
    );

    return (
        <Modal show={show} onHide={handleClose} centered size={isSubmitted ? 'md' : 'lg'}>
            <Modal.Header closeButton>
                {!isSubmitted && <Modal.Title as="h5" style={{ fontWeight: 'bold' }}>Hajj Package Enquiry</Modal.Title>}
            </Modal.Header>
            <Modal.Body className="p-4">
                {isSubmitted ? (
                    <ThankYouView />
                ) : (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={6}><Form.Group>
                                <Form.Label className="fw-semibold">Full Name*</Form.Label>
                                <Form.Control type="text" name="name" placeholder="Enter your full name" value={values.name} onChange={handleChange} isInvalid={!!errors.name} />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group></Col>
                            <Col md={6}><Form.Group>
                                <Form.Label className="fw-semibold">Phone Number*</Form.Label>
                                <Form.Control type="tel" name="phoneNumber" placeholder="Enter your phone number" value={values.phoneNumber} onChange={handleChange} isInvalid={!!errors.phoneNumber} />
                                <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
                            </Form.Group></Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}><Form.Group>
                                <Form.Label className="fw-semibold">Email ID*</Form.Label>
                                <Form.Control type="email" name="email" placeholder="Enter your email" value={values.email} onChange={handleChange} isInvalid={!!errors.email} />
                                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                            </Form.Group></Col>
                             <Col md={6}><Form.Group>
                                <Form.Label className="fw-semibold">Nationality</Form.Label>
                                <Form.Control type="text" name="nationality" placeholder="Enter your nationality" value={values.nationality} onChange={handleChange} isInvalid={!!errors.nationality} />
                                <Form.Control.Feedback type="invalid">{errors.nationality}</Form.Control.Feedback>
                            </Form.Group></Col>
                        </Row>

                        <hr />
                        <h6 className="fw-bold mb-3">Document Uploads (Optional)</h6>
                        
                        <Row className="mb-3">
                             <Col md={6}><Form.Group>
                                <Form.Label>Profile Photo</Form.Label>
                                <Form.Control type="file" name="profileFile" onChange={handleFileChange} />
                            </Form.Group></Col>
                             <Col md={6}><Form.Group>
                                <Form.Label>Vaccination Certificate</Form.Label>
                                <Form.Control type="file" name="vaccinationFile" onChange={handleFileChange} />
                            </Form.Group></Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}><Form.Group>
                                <Form.Label>Passport (Front Page)</Form.Label>
                                <Form.Control type="file" name="passportFrontFile" onChange={handleFileChange} />
                            </Form.Group></Col>
                             <Col md={6}><Form.Group>
                                <Form.Label>Passport (Back Page)</Form.Label>
                                <Form.Control type="file" name="passportBackFile" onChange={handleFileChange} />
                            </Form.Group></Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Aadhar Card (or National ID)</Form.Label>
                            <Form.Control type="file" name="aadharFile" onChange={handleFileChange} />
                        </Form.Group>
                        
                        <hr />
                        
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold">Additional Comments</Form.Label>
                            <Form.Control as="textarea" rows={3} name="comments" placeholder="Any specific requests or questions?" value={values.comments} onChange={handleChange}/>
                        </Form.Group>
                        
                        {errors.api && <p className="text-danger text-center">{errors.api}</p>}
                        
                        <div className="text-center mt-4">
                            <Button type="submit" disabled={isSubmitting} variant="primary" size="lg">
                                {isSubmitting ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Submit Enquiry'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default HajjEnquiryModal;